import { TopalValidationError } from './topal-validation-error';

export interface TopalXmlInput {
  employees: TopalEmployee[];
}

interface TopalEmployee {
  party: TopalParty;
  creditor: TopalCreditor;
  payMethod: TopalPayMethod;
  invoice: TopalInvoice;
  transaction: TopalTransaction;
  postings: TopalPosting[];
}

export interface TopalParty {
  languageCode: string;
  countryCode: string;
  shortName: string;
  name: string;
  zip: string;
  city: string;
  telephone: string;
  telefax: string;
  address1: string;
  address2: string;
  email: string;
  web: string;
  line1: string;
  line2: string;
  freePartyNum: string;
}

export interface TopalCreditor {
  freePartyNum: string;
  payTermCode: string;
  payMethodName: string;
}

export interface TopalPayMethod {
  freePartyNum: string;
  personRole: number;
  accountCode: string;
  payTypeFID: number;
  name: string;
  accountNum: string;
  bankCode: string;
}

export interface TopalInvoice {
  docNum: number;
  freePartyNum: string;
  personRoleFID: number;
  payTermCode: string;
  payMethodName: string;
  invoiceDate: string;
  totalAmount: number;
  freeInvoiceNum: string;
  text: string;
  remarks: string;
  exchangeRate?: number;
  totalAmountFC?: number;
}

export interface TopalTransaction {
  docNum: number;
  docDate: string;
  transactionTypeFID: number;
}

export interface TopalPosting {
  docNum: number;
  freeInvoiceNum: string;
  accountCode: string;
  postingTypeFID: number;
  amount: number;
  isDebit: boolean;
  text: string;
  vatCode?: string;
  vatAmount?: number;
  vatPercent?: number;
  exchangeRate?: number;
  fcAmount?: number;
  vatFcAmount?: number;
}

function escapeXml(value: string): string {
  const escaped = value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
  const sanitized = escaped.replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, '?');
  if (sanitized !== escaped) {
    console.warn(`escapeXml: non-Latin-1 characters replaced with "?" in: "${value}"`);
  }
  return sanitized;
}

function buildElement(name: string, fields: [string, string | number | boolean][]): string {
  const inner = fields
    .map(([key, value]) => {
      if (typeof value === 'boolean') return `    <${key}>${value}</${key}>`;
      if (typeof value === 'number') return `    <${key}>${value}</${key}>`;
      return `    <${key}>${escapeXml(String(value))}</${key}>`;
    })
    .join('\n');
  return `  <${name}>\n${inner}\n  </${name}>`;
}

function buildPartyElement(party: TopalParty): string {
  return buildElement('Party', [
    ['languageCode', party.languageCode],
    ['countryCode', party.countryCode],
    ['shortName', party.shortName],
    ['name', party.name],
    ['zip', party.zip],
    ['city', party.city],
    ['telephone', party.telephone],
    ['telefax', party.telefax],
    ['address1', party.address1],
    ['address2', party.address2],
    ['email', party.email],
    ['web', party.web],
    ['line1', party.line1],
    ['line2', party.line2],
    ['freePartyNum', party.freePartyNum],
  ]);
}

function buildCreditorElement(creditor: TopalCreditor): string {
  return buildElement('Creditor', [
    ['freePartyNum', creditor.freePartyNum],
    ['payTermCode', creditor.payTermCode],
    ['payMethodName', creditor.payMethodName],
  ]);
}

function buildPayMethodElement(payMethod: TopalPayMethod): string {
  return buildElement('PayMethod', [
    ['freePartyNum', payMethod.freePartyNum],
    ['personRole', payMethod.personRole],
    ['accountCode', payMethod.accountCode],
    ['payTypeFID', payMethod.payTypeFID],
    ['name', payMethod.name],
    ['accountNum', payMethod.accountNum],
    ['bankCode', payMethod.bankCode],
  ]);
}

function buildInvoiceElement(invoice: TopalInvoice): string {
  if (isNaN(invoice.totalAmount)) {
    throw new TopalValidationError(
      `Ungültiger Gesamtbetrag für Beleg Nr. ${invoice.docNum}`,
    );
  }
  const fields: [string, string | number | boolean][] = [
    ['docNum', invoice.docNum],
    ['freePartyNum', invoice.freePartyNum],
    ['personRoleFID', invoice.personRoleFID],
    ['payTermCode', invoice.payTermCode],
    ['payMethodName', invoice.payMethodName],
    ['invoiceDate', invoice.invoiceDate],
    ['totalAmount', invoice.totalAmount.toFixed(2)],
  ];
  if (invoice.totalAmountFC !== undefined) {
    fields.push(['totalAmountFC', invoice.totalAmountFC.toFixed(2)]);
    fields.push(['exchangeRate', invoice.exchangeRate ?? 1]);
  }
  fields.push(['freeInvoiceNum', invoice.freeInvoiceNum]);
  fields.push(['text', invoice.text]);
  fields.push(['remarks', invoice.remarks]);
  return buildElement('Invoice', fields);
}

function buildTransactionElement(transaction: TopalTransaction): string {
  return buildElement('Transaction', [
    ['docNum', transaction.docNum],
    ['docDate', transaction.docDate],
    ['transactionTypeFID', transaction.transactionTypeFID],
  ]);
}

function buildPostingElement(posting: TopalPosting): string {
  if (isNaN(posting.amount)) {
    throw new TopalValidationError(
      `Ungültiger Buchungsbetrag für Beleg Nr. ${posting.docNum}`,
    );
  }
  const fields: [string, string | number | boolean][] = [
    ['docNum', posting.docNum],
    ['freeInvoiceNum', posting.freeInvoiceNum],
    ['accountCode', posting.accountCode],
    ['postingTypeFID', posting.postingTypeFID],
    ['amount', posting.amount.toFixed(2)],
    ['isDebit', posting.isDebit],
    ['text', posting.text],
  ];

  if (posting.vatCode) {
    if (posting.vatAmount === undefined || posting.vatAmount === null) {
      throw new TopalValidationError(
        `Beleg Nr. ${posting.docNum}: MwSt.-Code "${posting.vatCode}" erfordert MwSt.-Betrag`,
      );
    }
    if (posting.vatPercent === undefined || posting.vatPercent === null) {
      throw new TopalValidationError(
        `Beleg Nr. ${posting.docNum}: MwSt.-Code "${posting.vatCode}" erfordert MwSt.-Satz`,
      );
    }
    fields.push(['vatCode', posting.vatCode]);
    fields.push(['vatAmount', posting.vatAmount.toFixed(2)]);
    fields.push(['vatPercent', posting.vatPercent]);
  }

  if (posting.exchangeRate !== undefined) {
    fields.push(['exchangeRate', posting.exchangeRate]);
    fields.push(['fcAmount', (posting.fcAmount ?? 0).toFixed(2)]);
  }
  if (posting.vatFcAmount !== undefined) {
    fields.push(['vatFcAmount', posting.vatFcAmount.toFixed(2)]);
  }

  return buildElement('Posting', fields);
}

export function buildTopalXml(input: TopalXmlInput): string {
  const parts: string[] = [
    '<?xml version="1.0" encoding="ISO-8859-1"?>',
    '',
    '<NewDataSet>',
  ];

  for (const employee of input.employees) {
    parts.push(buildPartyElement(employee.party));
    parts.push(buildCreditorElement(employee.creditor));
    parts.push(buildPayMethodElement(employee.payMethod));
    parts.push(buildInvoiceElement(employee.invoice));
    parts.push(buildTransactionElement(employee.transaction));
    for (const posting of employee.postings) {
      parts.push(buildPostingElement(posting));
    }
  }

  parts.push('</NewDataSet>');
  return parts.join('\n');
}
